import React, { useEffect, useRef, useState } from "react";
import * as Ariakit from "@ariakit/react";
import styled from "styled-components";
import { XCircle, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Color } from "../../../constants/color";
import { ZIndex } from "../../../constants/z-index";
import { CellaCorretta, ImportChatReply } from "../../../types";
import {
  correggiCelleImport,
  inviaMessaggioImport,
} from "../../../features/employee/utils";
import Suggerimento from "../../tooltip/suggerimento";
import TabellaRighe from "./tabella-righe";

/*
 L'importazione dei dipendenti condotta a conversazione.

 A sinistra la tabella delle righe, a destra l'assistente. La tabella e' lo strumento principale:
 e' la parte esatta, ed e' li' che i dati si correggono, cella per cella o a gruppi. L'assistente
 spiega e risponde, e accetta ancora i dati detti a voce. I numeri e gli esiti arrivano
 dall'analisi del backend e si aggiornano a ogni correzione e a ogni turno.

 Niente entra in anagrafica finche' l'operatore non lo dice qui dentro: il bottone CONFERMA
 manda il messaggio di conferma, ed e' l'unico punto da cui parte la scrittura.
*/

const Sfondo = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.DIALOG};
  background-color: ${Color.SHADOW};
  opacity: 0.5;
`;

const Riquadro = styled(Ariakit.Dialog)`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.DIALOG};
  margin: auto;
  height: min(48rem, calc(100dvh - 2rem));
  width: min(90rem, calc(100dvw - 2rem));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-style: hidden;
  border-radius: 1rem;
  background-color: ${Color.WHITE};
  box-shadow: 0 25px 50px -12px ${Color.SHADOW};
  font-family: "Avenir Next", sans-serif;

  &,
  & * {
    box-sizing: border-box;
  }
`;

const Intestazione = styled(Ariakit.DialogHeading)`
  position: relative;
  margin: 0;
  padding: 0.6rem 2.5rem;
  background-color: ${Color.BLUE};
  color: ${Color.WHITE};
  font-size: 1.3rem;
  font-weight: bold;
  text-align: center;
`;

const Chiudi = styled(Ariakit.DialogDismiss)`
  position: absolute;
  top: calc(50% - 12px);
  right: 0.6rem;
  background-color: ${Color.NONE};
  border-style: hidden;
  padding: 0;
  cursor: pointer;
  line-height: 0;
`;

const Corpo = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const Conversazione = styled.div`
  display: flex;
  flex: 0 0 min(22rem, 30%);
  flex-direction: column;
  min-width: 0;
  border-left: 1px solid ${Color.LIGHT_GREY};
`;

const Messaggi = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  row-gap: 0.7rem;
  overflow-y: auto;
  padding: 1rem;
`;

const Bolla = styled.div<{ $operatore: boolean }>`
  align-self: ${({ $operatore }) => ($operatore ? "flex-end" : "flex-start")};
  max-width: 85%;
  padding: 0.6rem 0.9rem;
  border-radius: 1rem;
  background-color: ${({ $operatore }) =>
    $operatore ? Color.BLUE : Color.LIGHTER_GREY};
  color: ${({ $operatore }) => ($operatore ? Color.WHITE : Color.DARK_GREY)};
  font-size: 0.95rem;
  line-height: 1.35;
  white-space: pre-wrap;
`;

const Scrittura = styled.form`
  display: flex;
  column-gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-top: 1px solid ${Color.LIGHT_GREY};
`;

const Campo = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid ${Color.LIGHT_GREY};
  border-radius: 0.6rem;
  font-family: inherit;
  font-size: 0.95rem;

  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
  }
`;

const Invia = styled.button`
  display: flex;
  align-items: center;
  padding: 0 0.8rem;
  border-style: hidden;
  border-radius: 0.6rem;
  background-color: ${Color.BLUE};
  color: ${Color.WHITE};
  cursor: pointer;

  &:disabled {
    background-color: ${Color.LIGHT_GREY};
    cursor: default;
  }
`;

const Bottoni = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 0.8rem;
  padding: 0.8rem 1rem;
  border-top: 1px solid ${Color.LIGHT_GREY};
`;

const Azione = styled.button<{ $colore: string }>`
  padding: 0.5rem 1.2rem;
  border-style: hidden;
  border-radius: 0.6rem;
  background-color: ${({ $colore }) => $colore};
  color: ${Color.WHITE};
  font-family: inherit;
  font-size: 0.9rem;
  cursor: pointer;

  &:disabled {
    background-color: ${Color.LIGHT_GREY};
    cursor: default;
  }
`;

type Turno = { operatore: boolean; testo: string };

interface Props {
  aperta: boolean;
  // Il primo turno: l'apertura arriva gia' fatta da chi ha consegnato il foglio.
  apertura: ImportChatReply;
  onChiudi: () => void;
  // Chiamata a importazione avvenuta, perche' la pagina possa rileggere l'anagrafica.
  onImportata: () => void;
}

const ImportChat = ({ aperta, apertura, onChiudi, onImportata }: Props) => {
  const { t } = useTranslation();
  const [turni, setTurni] = useState<Turno[]>([
    { operatore: false, testo: apertura.message },
  ]);
  const [stato, setStato] = useState<ImportChatReply>(apertura);
  const [testo, setTesto] = useState("");
  const [inAttesa, setInAttesa] = useState(false);
  const [correggendo, setCorreggendo] = useState(false);
  const fondo = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: "smooth" });
  }, [turni]);

  const manda = async (messaggio: string) => {
    if (!messaggio.trim() || inAttesa || correggendo || stato.completed) {
      return;
    }
    setTurni((precedenti) => [
      ...precedenti,
      { operatore: true, testo: messaggio },
    ]);
    setTesto("");
    setInAttesa(true);

    const risposta = await inviaMessaggioImport(stato.sessionId, messaggio);
    setInAttesa(false);
    if (!risposta) {
      return;
    }
    setStato(risposta);
    setTurni((precedenti) => [
      ...precedenti,
      { operatore: false, testo: risposta.message },
    ]);
    if (risposta.completed && risposta.imported) {
      onImportata();
    }
  };

  /*
   Una correzione dalla tabella non e' un turno della conversazione: la risposta aggiorna la
   tabella e i conteggi, e l'assistente resta zitto.
  */
  const correggi = async (celle: CellaCorretta[]) => {
    if (celle.length === 0 || stato.completed) {
      return false;
    }
    setCorreggendo(true);
    const risposta = await correggiCelleImport(stato.sessionId, celle);
    setCorreggendo(false);
    if (!risposta) {
      return false;
    }
    setStato(risposta);
    return true;
  };

  const analisi = stato.analysis;
  const nienteDaImportare = analisi.toCreate + analisi.toUpdate === 0;

  return (
    <Riquadro
      open={aperta}
      onClose={onChiudi}
      backdrop={<Sfondo />}
      // Esc dentro una cella della tabella annulla la correzione, non l'importazione: chiudere
      // qui butterebbe via la bozza con tutte le correzioni fatte.
      hideOnEscape={(evento) =>
        !(evento.target instanceof HTMLElement && evento.target.closest("table"))
      }
    >
      <Intestazione render={<h2 />}>
        {t("import-chat-titolo")}
        <Chiudi aria-label="Chiudi" onClick={onChiudi}>
          <XCircle size={24} color={Color.WHITE} />
        </Chiudi>
      </Intestazione>

      <Corpo>
        <TabellaRighe
          analisi={analisi}
          modificabile={!stato.completed && !inAttesa && !correggendo}
          onCorreggi={correggi}
        />

        <Conversazione>
          <Messaggi>
            {turni.map((turno, indice) => (
              <Bolla key={indice} $operatore={turno.operatore}>
                {turno.testo}
              </Bolla>
            ))}
            {inAttesa && (
              <Bolla $operatore={false}>{t("import-chat-in-attesa")}</Bolla>
            )}
            <div ref={fondo} />
          </Messaggi>

          <Scrittura
            onSubmit={(evento) => {
              evento.preventDefault();
              void manda(testo);
            }}
          >
            <Campo
              value={testo}
              disabled={stato.completed || inAttesa}
              placeholder={t("import-chat-placeholder")}
              onChange={(evento) => setTesto(evento.target.value)}
            />
            <Invia
              type="submit"
              disabled={stato.completed || inAttesa || !testo.trim()}
              aria-label={t("import-chat-invia")}
            >
              <Send size={18} />
            </Invia>
          </Scrittura>
        </Conversazione>
      </Corpo>

      <Bottoni>
        {stato.completed ? (
          <Azione type="button" $colore={Color.GREY} onClick={onChiudi}>
            {t("import-chat-chiudi")}
          </Azione>
        ) : (
          <Suggerimento testo={t("import-chat-aiuto-annulla")}>
            <Azione
              type="button"
              $colore={Color.GREY}
              disabled={inAttesa || correggendo}
              onClick={onChiudi}
            >
              {t("import-chat-annulla")}
            </Azione>
          </Suggerimento>
        )}
        <Suggerimento
          testo={
            nienteDaImportare
              ? t("import-chat-aiuto-conferma-vuoto")
              : t("import-chat-aiuto-conferma")
          }
        >
          <Azione
            type="button"
            $colore={Color.GREEN}
            disabled={
              stato.completed || inAttesa || correggendo || nienteDaImportare
            }
            onClick={() => void manda(t("import-chat-parola-conferma"))}
          >
            {t("import-chat-conferma")}
          </Azione>
        </Suggerimento>
      </Bottoni>
    </Riquadro>
  );
};

export default ImportChat;
